import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Form, Modal, Steps, Alert } from 'antd';
import useForm from '../../shared/useFormHook';
import Input from '../../shared/input/Input';
import moment from 'moment';
import { ApiConstant } from '../../../../constants';
import axios from 'axios';
import SkillsSelector from './SkillsSelector';
import TuringHelpBtn from '../../shared/TuringHelpBtn/TuringHelpBtn';
import { pushLog2DV2LogServer } from '../../../../services/logging';
import { DirectionIcon, ProjectIcon, WorkExpDeleteIcon, CaretUpEmberIcon } from '../../../components/common/CustomIcon';
import useFlagsFromBackend, { GB_FLAGS } from '../../../services/launchDarkly';
import { checkFlagValueIsV1orV2 } from '../../../../services/util/checkFlagValue';
import { UploadImage } from './ProfileAndWorkhistorySection/sections/Workhistory.section';

const { Step } = Steps;

function WorkHistoryForm ({ service, customClass }) {
  const { onPanelSelect, onSaveClick, removeEvent, save, addSugg, setCompleted, handleUpdatePersonalInformation } = service;
  const { WorkHistory, PersonalInformation } = service.state;
  const { isReadOnly } = service.props;
  const [addNew, setAddNew] = useState(false);
  const [newItem, setNewItem] = useState(null);
  const { data, bind, bindCheckbox, setData, getData, setError, resetError, getChanges } = useForm({}, service.saveDebounce);
  // This is to remove all the fields from basic profile that we removed from onboarding
  const { $flagsReady, use_remove_update_fields_in_onboarding_form_v2 } = useFlagsFromBackend([GB_FLAGS.use_remove_update_fields_in_onboarding_form_v2]);
  useEffect(() => {
    setData(PersonalInformation);
  }, [PersonalInformation]);

  const onSave = useCallback(async (isAutoSave) => {
    resetError();
    const values = await getData();
    let success = true;
    let successResult = true;
    const fields = ['freelance_work_experience', 'freelance_work_experience_years'];
    const changedFields = await getChanges(PersonalInformation, fields);
    if (changedFields.length) {
      if (values.freelance_work_experience && (!values.freelance_work_experience_years || !parseInt(values.freelance_work_experience_years))) {
        if (!isAutoSave) {
          setError('freelance_work_experience_years');
        }
        return false;
      } else {
        success = await handleUpdatePersonalInformation(values, isAutoSave);
        if (success && isAutoSave) {
          successResult = 'WorkHistory';
          service.setState({ PersonalInformation: { ...PersonalInformation, ...values } });
        }
      }
    }

    let listItemsToSave = [...WorkHistory];
    if (newItem && addNew && !isAutoSave) listItemsToSave.push(newItem);
    listItemsToSave = listItemsToSave.map(item => item.save(isAutoSave));

    success = success && (await Promise.all(listItemsToSave)).every(success => {
      if (success === 'WorkHistory') successResult = 'WorkHistory';
      return success;
    });

    if (!isAutoSave) {
      if (success) {
        setAddNew(false);
        setNewItem(null);
      } else {
        onPanelSelect('WorkHistory');
        setCompleted('WorkHistory', false);
      }
    }
    return success ? successResult : false;
  }, [newItem, addNew, WorkHistory, PersonalInformation]);

  useEffect(() => {
    addSugg('WorkHistory', { id: 'WorkHistory1', content: <p>List all the roles you have held.</p> });
    addSugg('WorkHistory', { id: 'WorkHistory2', content: <p>Include the name of the company.</p> });
    addSugg('WorkHistory', { id: 'WorkHistory3', content: <p>Add the dates of employment.</p> });
    addSugg('WorkHistory', { id: 'WorkHistory4', content: <p>Tell us about the projects you worked on.</p> });
    addSugg('WorkHistory', {
      id: 'WorkHistory5',
      content: (<p>Make sure your roles and experience in tech skills matches what you filled in the Profile.</p>)
    });
    onSaveClick('WorkHistory', onSave);
    return () => removeEvent('WorkHistory');
  }, [onSave]);

  const addNewItem = () => {
    if (!newItem) {
      const item = {
        key: (new Date()).getTime()
      };
      setNewItem(item);
    }
    setAddNew(true);
  };

  const handleCancelAddingNewItem = () => {
    setNewItem(null);
    setAddNew(false);
  };

  const onFreelanceKeyUp = (el) => {
    if (el.value !== '') {
      if (parseInt(el.target.value) < parseInt(el.target.min)) {
        el.target.value = el.target.min;
      }
      if (parseInt(el.target.value) > parseInt(el.target.max)) {
        el.target.value = el.target.max;
      }
    }
  };

  return (
    <div className={customClass || ''}>
      <div className="TuringStepsFormv2">
        <p>Your work experience is the most important section for a hiring manager to see if you’re a good fit for their job.</p>
        {
          $flagsReady && !checkFlagValueIsV1orV2(use_remove_update_fields_in_onboarding_form_v2) ? (
            <div className="WorkHistory-freelance">
              <Form.Item label="Do you have freelance work experience?">
                <div className="frmcheckbox">
                  <Checkbox {...bindCheckbox('freelance_work_experience', { reversed: true })}>No</Checkbox>
                  <div className="frmcheckbox1">
                    <Checkbox {...bindCheckbox('freelance_work_experience')}>Yes, I have</Checkbox>
                    <input type="number" className="ant-input freelance_year_work_exp_input" onKeyUp={onFreelanceKeyUp} min={0} max={50} disabled={!data.freelance_work_experience} {...bind('freelance_work_experience_years')} />
                    <span>years of freelance work experience</span>
                  </div>
                </div>
              </Form.Item>
            </div>
          ) : null
        }
        <Steps current={WorkHistory.length - 1} direction="vertical">
          {WorkHistory.map(item => <WorkHistoryItem key={item.id || item.key} service={service} item={item} onSave={() => save('WorkHistory')} />)}
          {addNew ? (
            <WorkHistoryItem service={service} item={newItem} onSave={() => save('WorkHistory')} cancelAddingNewItem={handleCancelAddingNewItem} />
          ) : (isReadOnly ? null : (
            <Step icon={<DirectionIcon />} description={<span className="additembtn" onClick={addNewItem}><span className="material-icons" aria-hidden="true">add</span> Add position</span>} />
          ))}
        </Steps>
      </div>
      {isReadOnly ? null : (
        <div className="FinalizeResumeV2FormBtns">
          <Button onClick={() => { setAddNew(false); onPanelSelect(null); }}>Cancel</Button>
          <Button type="primary" onClick={() => save('WorkHistory')}>Update information</Button>
        </div>
      )}
    </div>
  );
}

WorkHistoryForm.propTypes = {
  service: PropTypes.any,
  customClass: PropTypes.string
};

const years = [];
for (let i = 0; i <= 35; i++) {
  years.push({
    value: new Date().getFullYear() - i,
    label: new Date().getFullYear() - i
  });
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
].map((month) => ({ value: month, label: month }));

const employmentTypes = ['Full Time', 'Part Time', 'Freelance', 'Self-Employed', 'Contract', 'Internship', 'Other'].map((value) => ({ value, label: value }));
let saved_files = [];

const WorkHistoryItem = ({ item, service, onSave, ...props }) => {
  const [open, setOpen] = useState(false);
  const { addSugg, deleteSugg, loadData } = service;
  const { baseSkills, skillInfo } = service.state;
  const { isReadOnly, isEmbed } = service.props;
  const { data, bind, setData, getData, setValue, setError, getErrors, resetError, getChanges, errors } = useForm({}, service.saveDebounce);

  const [showInfoMissing, setShowInfoMissing] = useState(false);
  const [uploaded_files_data, setUploadFileData] = useState([]);

  useEffect(() => {
    if (!item.id) {
      setOpen(true);
    }
  }, [item.id]);

  useEffect(() => {
    if (service.state.focusProject && service.state.focusProject.work_history_id === item.id && item.id) {
      setOpen(true);
    }
  }, [service && service.state.focusProject]);

  const initProject = (project) => {
    project.files = project.files || [];
    project.deletedFiles = project.deletedFiles || [];
    project.set = (data = {}) => {
      Object.assign(project, data);
      setValue('projects', [...item.projects], !project.id);
    };
    project.$delete = () => {
      if (project.id) {
        project.set({ $deleted: true });
        if (onSave) onSave();
      } else {
        item.projects = item.projects.filter(p => p !== project);
        setValue('projects', [...item.projects], !project.id);
      }
    };
    return project;
  };

  const addNewProjectItem = () => {
    item.projects = [...item.projects, initProject({ key: (new Date()).getTime() })];
    setValue('projects', [...item.projects]);
  };

  useEffect(() => {
    deleteSugg('WorkHistory', 'WorkHistory1');
    deleteSugg('WorkHistory', 'WorkHistory2');
    deleteSugg('WorkHistory', 'WorkHistory3');
    deleteSugg('WorkHistory', 'WorkHistory4');
    if (data && (data.projects || []).find(project => !project.$deleted && !project.id)) {
      addSugg('WorkHistory', {
        id: 'WorkHistory1',
        content: (
          <div>
            <p>Projects is the best way to showcase your work. Be as detailed as possible and don&apos;t forget to:</p>
            <ul style={{ padding: 0 }}>
              <li>Mention your tech-stacks at least 3 times.</li>
              <li>Describe your project in, at least, 200 words.</li>
              <li>Add a link where we can see more about this project.</li>
              <li>Attach screenshot for a quick glance to what you created.</li>
            </ul>
          </div>
        )
      });
      addSugg('WorkHistory', { id: 'WorkHistory2', content: (<p>What was the goal of the project?</p>) });
      addSugg('WorkHistory', { id: 'WorkHistory3', content: (<p>Mention the tech skills you utilized.</p>) });
      addSugg('WorkHistory', { id: 'WorkHistory4', content: (<p>Highlight the achievements.</p>) });
    } else if (data && (data.projects || []).find(project => !project.$deleted)) {
      addSugg('WorkHistory', { id: 'WorkHistory2', content: (<p>What was the goal of the project?</p>) });
      addSugg('WorkHistory', { id: 'WorkHistory3', content: (<p>Mention the tech skills you utilized.</p>) });
      addSugg('WorkHistory', { id: 'WorkHistory4', content: (<p>Highlight the achievements.</p>) });
    }
  }, [data.projects]);

  const validateFields = (workhistoryItem) => {
    if (!workhistoryItem.id) return;
    const fields = 
      ['position', 'company', 'start_month', 'start_year', 'employment_type'];
    const projFields = ['project_name', 'details', 'outcome', 'start_month', 'start_year'];
    const extra_fields = ['end_month', 'end_year'];

    if (workhistoryItem.end_year !== 0) {
      fields.push(...extra_fields);
    }
    fields.forEach(field => {
      if (!workhistoryItem[field]) {
        setError(field);
      }
    });

    const projects = workhistoryItem.projects || [];

    for (let i = 0; i < projects.length; i++) {
      const project = projects[i];
      const projectFieldsPresent = [...projFields, ...(project.end_year !== 0 ? extra_fields : [])].every(field => project[field]);
      if (!projectFieldsPresent) {
        setShowInfoMissing(true);
        return;
      }
    }
    setShowInfoMissing(false);
  };

  useEffect(() => {
    if (!item) return;
    item.projects = (item.projects || []).map(initProject);
    setData(item);
    resetError();

    validateFields(item);
    item.save = async (isAutoSave) => {
      if (!isAutoSave) resetError();
      const fields = 
        ['position', 'company', 'start_month', 'start_year', 'employment_type'];
      let data = await getData();
      if (data.end_year !== 0) {
        fields.push('end_month');
        fields.push('end_year');
      }

      let someProjectChange = false;
      const changedFields = await getChanges(item, [...fields]);
      const someProjectChangePromise = await Promise.all(data.projects.map(project => project.changed ? project.changed() : Promise.resolve(false)));
      someProjectChange = someProjectChangePromise.some(success => success);

      if (!changedFields.length && !someProjectChange) return true;
      if (isAutoSave && !data.id) return false;
      if (isAutoSave) {
        if (fields.find(field => !data[field])) {
          return false;
        }
      } else {
        fields.forEach(field => {
          if (!data[field]) {
            setError(field);
          }
        });
      }

      if (data.start_month && data.start_year && data.end_year && data.end_month) {
        const getDate = (start_end) => {
          const month = data[`${start_end}_month`];
          const year = data[`${start_end}_year`];
          if (month && year) return moment(`${year}-${month}`, 'YYYY-MMMM');
          return null;
        };
        const start_date = getDate('start');
        const end_date = getDate('end');
        if (moment(start_date.format('YYYY-MM-01')).isAfter(end_date.format('YYYY-MM-01'))) {
          if (isAutoSave) return false;
          setError('start_month');
          setError('start_year');
          setError('end_month');
          setError('end_year');
        }
      }

      const errors = await getErrors();
      if (errors.length) {
        if (isAutoSave) return false;
        setOpen(true);
      } else {
        const projectsSave = await Promise.all(data.projects.map(project => project.save ? project.save(isAutoSave) : Promise.resolve(true)));
        if (!projectsSave.every(success => success)) return false;
        data = await getData();

        const dataForm = new FormData();
        const projects = data.projects.map((project, i) => {
          const files = project.files.map((file, j) => {
            if (file.originFileObj && saved_files.indexOf(file.uid) < 0) {
              dataForm.append(`file_${i}_${j}`, file.originFileObj);
              return { fileId: `file_${i}_${j}`, uid: file.uid, name: file.name };
            }
            return file;
          }).filter(file => file.fileId);
          return {
            ...project,
            files,
            deletedFiles: project.deletedFiles || []
          };
        });

        const formatProject = project => {
          const file_to_save = (project.files || []).filter(f => saved_files.indexOf(f.uid) < 0);
          const fileIds = file_to_save.map((p) => p.uid);
          saved_files = saved_files.concat(fileIds);
          return {
            id: project.id,
            project_name: project.project_name,
            url: project.url,
            details: project.details,
            outcome: project.outcome,
            skills: (project.skills || []).map(skill => skill.skill_id),
            files: file_to_save,
            start_month: project.start_month,
            start_year: project.start_year,
            end_month: project.end_month,
            end_year: project.end_year,
            deletedFiles: project.deletedFiles || []
          };
        };

        const insertProjects = projects.filter(project => !project.$deleted && project.$added).map(formatProject);
        const updateProjects = projects.filter(project => project.$changed && !project.$added && !project.$deleted).map(formatProject);
        const deleteProjects = isAutoSave ? [] : projects.filter(project => project.$deleted && project.id).map(project => project.id);

        dataForm.append('data', JSON.stringify({
          ...data,
          skills: (data.skills || []).map(skill => skill.skill_id),
          projects: [],
          insertProjects,
          updateProjects,
          deleteProjects
        }));
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: token
        };
        const endPoint = data.id ? `onboarding/edit-workhistory/${data.id}` : 'onboarding/save-workhistory';
        return axios
          .post(`${ApiConstant.BASE_URL}${endPoint}`, dataForm, { headers })
          .then(result => result.data)
          .then((resultData) => {
            if (resultData.success) {
              const upload_data = resultData.upload_data;
              let currentUploadData = uploaded_files_data;
              if (Array.isArray(upload_data) && upload_data.length && upload_data[0].uploaded_files) {
                const existingProjectIndex = (currentUploadData || []).findIndex(i => i.project_id === upload_data[0].project_id);
                if (existingProjectIndex >= 0) {
                  currentUploadData[existingProjectIndex].uploaded_files = currentUploadData[existingProjectIndex].uploaded_files.concat(upload_data[0].uploaded_files);
                } else {
                  currentUploadData = currentUploadData.concat(upload_data);
                }
                setUploadFileData(currentUploadData);
              }
              resultData.item.projects.map(project => {
                const p = projects.filter(pp => !pp.$deleted).find(pp => (pp.id && pp.id === project.id) || (pp.$added && pp.project_name === project.project_name));
                if (p) {
                  const skillsInfos = p.skills.reduce((acc, skill) => {
                    if (skill.auto_tagging) {
                      acc.autoTaggingSkills.push({
                        skill: skill.skill_name,
                        id: skill.skill_id
                      });
                    } else {
                      acc.manualAddingSkills.push({
                        skill: skill.skill_name,
                        id: skill.skill_id
                      });
                    }
                    return acc;
                  }, { autoTaggingSkills: [], manualAddingSkills: [] });
                  pushLog2DV2LogServer.info(
                    'AUTO_TAGGING_SKILLS',
                    'AUTO_TAGGING_SKILLS',
                    {},
                    { user_id: project.developer_id, project_id: project.id, ...skillsInfos },
                    {}
                  );
                }
              });
              if (isAutoSave) {
                resultData.item.projects.map(project => {
                  const p = data.projects.filter(pp => !pp.$deleted).find(pp => pp.$added && pp.project_name === project.project_name);
                  if (p) {
                    p.setId(project.id);
                  }
                });
                data.projects.map(project => project.id && project.acceptChanges ? project.acceptChanges() : null);
                service.setState((s) => {
                  return { ...s, WorkHistory: s.WorkHistory.map(elm => elm.id === data.id ? { ...elm, ...data } : elm) };
                });
              }
            }
            return 'WorkHistory';
          })
          .catch(error => {
            const { message, stack, code } = error;
            console.error(message, stack, code);
          });
      }
      return errors.length === 0;
    };

    item.delete = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      Modal.confirm({
        className: 'delete-item-modal',
        title: (
          <div className="Delete-work-experience-item">
            <h1>Delete Position</h1>
            {item.id ? (
              <div className="Delete-work-experience-item-details">
                <span>
                  <b>{item.position}</b>
                  <span>/</span>
                  <span className="WorkHistoryItem-company">{item.company}</span>
                </span>
              </div>
            ) : null}
          </div>
        ),
        content: <p className="Delete-work-experience-item-p">Be sure you want to delete this Position, after deleting you won&apos;t be able to get it back.</p>,
        okText: 'Delete Position',
        okType: 'danger',
        cancelText: 'Cancel',
        iconType: null,
        width: 400,
        onOk: () => {
          if (!item.id) {
            if (props.cancelAddingNewItem) props.cancelAddingNewItem();
            return;
          }
          const token = localStorage.getItem('token');
          const headers = {
            'Content-Type': 'application/json',
            Authorization: token
          };
          return axios
            .delete(`${ApiConstant.BASE_URL}onboarding/delete-workhistory/${item.id}`, {}, { headers })
            .then(result => {
              loadData(['WorkHistory']);
            })
            .catch(error => {
              const { message, stack, code } = error;
              console.error(message, stack, code);
            });
        }
      });
    };
  }, [item]);

  const listErrors = (error) => {
    let displayedErrors = '';
    switch (error) {
      case 'position':
        displayedErrors = ' Add position for this work experience';
        break;
      case 'company':
        displayedErrors = ' Add the name of the company for this work position';
        break;
      case 'start_month':
        displayedErrors = ' Add start month for this job position';
        break;
      case 'start_year':
        displayedErrors = ' Add start year for this job position';
        break;
      case 'end_month':
        displayedErrors = ' Add end month for this job position';
        break;
      case 'end_year':
        displayedErrors = ' Add end year for this job position';
        break;
      case 'employment_type':
        displayedErrors = ' Add employment type for this job position';
        break;
      default:
        displayedErrors = ` Add ${error} for this job position`;
        break;
    }
    return displayedErrors;
  };

  const TitleStyle = (position, company) => {
    const len = (position + '/' + company).length;
    if (errors.length || showInfoMissing) {
      if (len > 26) return 'WorkHistoryItem-title2';
      if (len > 13 && len <= 26) return 'WorkHistoryItem-title3';
    }
    return 'WorkHistoryItem-title';
  };

  const errorTag = (position, company) => {
    const len = (position + '/' + company).length;
    if (len > 13 && len < 26) return 'WorkHistoryItem-missing-information-left-margin3';
    if (len <= 13) return 'WorkHistoryItem-missing-information-left-margin2';
    return 'WorkHistoryItem-missing-information-left-margin';
  };

  let title = (
    <div className='WorkHistory-flex' onClick={() => setOpen(!open) }>
      <div className={TitleStyle(item.position, item.company)} data-heap-redact-text>
        {item.position}
        <span style={{ color: '#ccc' }}>/</span>
        <span className="WorkHistoryItem-company">{item.company}</span>
      </div>
      { errors.length || showInfoMissing
        ? <span className={errorTag(item.position, item.company)}><Alert className='resume-warning-alert' message="Missing Information" showIcon type='error' icon={<CaretUpEmberIcon className="resume-warning-icon" />} /></span>
        : null}
      <div className='deleteAndIcon'>
        {open && !isReadOnly ? <span className="deleteIconMarginLeft" onClick={item.delete}> <WorkExpDeleteIcon /></span> : null }
        <span className="material-icons openClose" aria-hidden="true">{open ? 'expand_less' : 'expand_more'}</span>
      </div>
    </div>
  );

  const getErrorMessages = () => errors.map((err, i) => {
    if (data.end_year === 0 && ['end_year', 'end_month'].includes(err.name)) return null;
    return <span key={i} className=''><strong>!</strong>{listErrors(err.name)}</span>;
  });

  if (!item.id) {
    title = (
      <div className="WorkHistoryItem-title">
        Add a new position
        <span style={{ flex: 1 }} />
        <span onClick={item.delete}><WorkExpDeleteIcon /></span>
      </div>
    );
  }

  let form = null;
  if (open) {
    form = (
      <div className="WorkHistoryItem-form">
        <div className='listErrors'>
          {!errors.length ? null : getErrorMessages()}
        </div>
        <div className="a"><Input label="Position" placeholder="Position / Role" {...bind('position')} /></div>
        <div className="b"><Input label="Company" placeholder="Company / Business" {...bind('company')} /></div>
        <div className="c WorkHistoryItem-date"><Input.Select options={months} label="Starting date" {...bind('start_month')} /></div>
        <div className="d WorkHistoryItem-date"><Input.Select options={years} label="" {...bind('start_year')} /></div>
        <div className="e WorkHistoryItem-date">{data.end_year !== 0 ? <Input.Select options={months} label="Ending date" {...bind('end_month')} /> : null}</div>
        <div className="f WorkHistoryItem-date">{data.end_year !== 0 ? <Input.Select options={years} label="" {...bind('end_year')} /> : null}</div>
        <div className="g">
          <Checkbox
            checked={data.end_year === 0}
            onChange={(e) => setValue('end_year', e.target.checked ? 0 : (item.end_year === 0 ? null : (item.end_year || null)))}
          >
            Still working here
          </Checkbox>
        </div>
        {/* <div className="h">
          <Input.TextArea label={<TuringHelpBtn help="Describe your overall experience in terms of duties assigned and skills used. Start with explaining how you fit into the company structure, your previous role, and how you contributed in a team setting. Stating examples will make your description more compelling.">Experience description</TuringHelpBtn>} {...bind('details')} />
        </div>
        <div className="i">
          <SkillsSelector
            selectSkillAsObject
            autoTagText={data.details}
            label={<TuringHelpBtn help="The skills used in this job role will be automatically added here. You can also add missing and other related tech stacks manually.">Tech stacks</TuringHelpBtn>}
            {...bind('skills')}
            allSkills={baseSkills}
            passed_skills={skillInfo.passed_skills}
            skills_inprogress={skillInfo.skills_inprogress}
            userAllSkills={skillInfo.all_skills}
          />
        </div> */}
        <div className="emptype">
          <Input.Radio options={employmentTypes} label="Employment type" {...bind('employment_type')} />
        </div>
        <div className="j WorkHistoryItemProjects">
          {isEmbed ? (
            <div className="roleatthecompany">
              <Input.TextArea
                label="What was your role at the company?"
                placeholder="What was your role at the company?"
                {...bind('details')}
              />
            </div>
          ) : null}
          <div className="TuringStepsFormv2-title WorkHistoryItemProjects-titles"><span className="WorkHistoryItemProjects-title"><span><ProjectIcon /></span>Projects</span></div>
          <Steps current={data.projects.length - 1} direction="vertical">
            {data.projects.map((project, i) => <WorkHistoryItemProjectItem upload_data={uploaded_files_data} key={i} item={project} parent={data} service={service} showError/>)}
            {data.projects.find(p => !p.id) || isReadOnly ? null : (
              <Step icon={<DirectionIcon />} className="WorkHistoryItemProjectsaddnew" description={(
                <div className="addNewProjectItemcontainer">
                  <span className="additembtn addNewProjectItem" onClick={addNewProjectItem}><span className="material-icons" aria-hidden="true">add</span> Add a project</span>
                </div>
              )} />
            )}
          </Steps>
        </div>
      </div>
    );
  }

  return (
    <Step icon={<DirectionIcon active={open} />} className={open ? 'openStep' : 'step-custom-fill'} title={title} description={form} {...props} />
  );
};

WorkHistoryItem.propTypes = {
  item: PropTypes.any,
  service: PropTypes.any,
  cancelAddingNewItem: PropTypes.any,
  onSave: PropTypes.any
};

const WorkHistoryItemProjectItem = ({ upload_data, item, service, parent, ...props }) => {
  const [open, setOpen] = useState(false);
  const formElm = useRef(null);
  const { baseSkills, skillInfo } = service.state;
  const { isReadOnly } = service.props;
  const { data, bind, setData, getData, setError, getErrors, resetError, getChanges, acceptChanges, setValue, errors, setEnableAutoSave } = useForm({}, service.saveDebounce);
  useEffect(() => {
    if (!item.id) {
      setOpen(true);
    }
    setEnableAutoSave(true);
  }, [item.id]);

  useEffect(() => {
    if (service.state.focusProject && service.state.focusProject.project_id === item.id && item.id) {
      setOpen(true);
    }
    if (service.state.focusProject && service.state.focusProject.project_id === item.id && item.id && formElm.current) {
      const elm = formElm.current.querySelector('textarea');
      if (elm) {
        setTimeout(() => {
          elm.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          elm.classList.add('focusAnimation');
          setTimeout(() => {
            elm.classList.remove('focusAnimation');
          }, 2000);
          service.setState({ focusProject: null });
        }, 1000);
      } else {
        formElm.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }
  }, [service.state.focusProject, formElm.current]);

  const validateFields = (project) => {
    if (!project.id) return;
    const fields = ['project_name', 'details', 'outcome', 'start_month', 'start_year'];

    if (project.end_year !== 0) {
      fields.push('end_month', 'end_year');
    }
    fields.forEach(field => {
      if (!project[field]) {
        setError(field);
      }
    });
  };

  useEffect(() => {
    if (!item) return;
    setData(item);
    resetError();
    validateFields(item);
    const fields = ['project_name', 'details', 'outcome', 'skills', 'url', 'start_month', 'start_year', 'end_month', 'end_year', 'files', 'deletedFiles'];
    item.changed = async () => {
      const changedFields = await getChanges(item, fields);
      return !!changedFields.length || item.$deleted;
    };

    item.acceptChanges = acceptChanges;

    item.setId = (id) => {
      item.id = id;
      setValue('id', id);
      delete item.$added;
      acceptChanges();
    };

    item.save = async (isAutoSave) => {
      if (!isAutoSave) resetError();
      if (!(await item.changed())) return true;
      const data = await getData();
      let listFields = [...fields.filter(name => !['url', 'files'].includes(name))];

      if (data.end_year === 0) {
        listFields = listFields.filter(name => !['end_month', 'end_year'].includes(name));
      }

      if (isAutoSave) {
        if (listFields.find(field => {
          if (field === 'files') {
            if (!Array.isArray(data[field]) || !data[field].length) {
              return true;
            }
          } else {
            if (!data[field]) {
              return true;
            }
          }
        })) {
          return false;
        }
      } else {
        listFields.forEach(field => {
          if (field === 'files') {
            if (!Array.isArray(data[field]) || !data[field].length) {
              setError(field);
            }
          } else {
            if (!data[field]) {
              setError(field);
            }
          }
        });
      }

      if (data.start_month && data.start_year && data.end_year && data.end_month) {
        const getDate = (start_end) => {
          const month = data[`${start_end}_month`];
          const year = data[`${start_end}_year`];
          if (month && year) return moment(`${year}-${month}`, 'YYYY-MMMM');
          return null;
        };
        const start_date = getDate('start');
        const end_date = getDate('end');
        if (moment(start_date.format('YYYY-MM-01')).isAfter(end_date.format('YYYY-MM-01'))) {
          if (isAutoSave) return false;
          setError('start_month');
          setError('start_year');
          setError('end_month');
          setError('end_year');
        }
      }

      const errors = await getErrors();
      if (errors.length) {
        if (isAutoSave) return false;
        setOpen(true);
      } else {
        item.$changed = !!item.id;
        item.$added = !item.id;
        await acceptChanges(item, fields);
        item.set();
      }
      return errors.length === 0;
    };

    item.delete = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      Modal.confirm({
        className: 'delete-item-modal',
        title: (
          <div className="Delete-work-experience-item">
            <h1 className="font-medium">Delete Project</h1>
            {item.id ? (
              <div className="Delete-work-experience-item-details">
                {parent.id ? (
                  <span>
                    <span className="font-medium color-aaa">{parent.position}</span>
                    <span>/</span>
                    <span className="WorkHistoryItem-company color-aaa">{parent.company}</span>
                  </span>
                ) : null}
                <b>{item.project_name}</b>
              </div>
            ) : null}
          </div>
        ),
        content: <p className="Delete-work-experience-item-p">Be sure you want to delete this Project, after deleting you won&apos;t be able to get it back.</p>,
        okText: 'Delete Project',
        okType: 'danger',
        cancelText: 'Cancel',
        iconType: null,
        width: 400,
        onOk: () => {
          item.$delete();
        }
      });
    };
    item.set();
    return () => {
      item.changed = null;
      item.save = null;
    };
  }, [item]);

  const onFilesUpdate = (files) => {
    setValue('files', [...files]);
  };

  const onFilesDelete = async (file) => {
    const data = await getData();
    if (file.project_file_id) {
      setValue('deletedFiles', [...data.deletedFiles, file]);
    } else {
      const project_files = upload_data.find(i => i.project_id === item.id);
      if (project_files && Array.isArray(project_files.uploaded_files) && project_files.uploaded_files.length) {
        const uploaded_file_info = project_files.uploaded_files.find(f => f.name === `${file.uid}_${file.name}`);
        if (!uploaded_file_info) return;
        const new_deleted_data = {
          project_file_id: uploaded_file_info.file_id,
          name: uploaded_file_info.name
        };
        setValue('deletedFiles', [...data.deletedFiles, new_deleted_data]);
      }
    }
  };

  const TitleStyle = (project) => {
    if (!project) return 'WorkHistoryItemProjectItem-title';
    const len = project.length;
    if (errors.length) {
      if (len > 44) return 'WorkHistoryItemProjectItem-title3';
      if (len > 35 && len < 44) return 'WorkHistoryItemProjectItem-title2';
    } else {
      if (len >= 44) return 'WorkHistoryItemProjectItem-title2';
    }
    return 'WorkHistoryItemProjectItem-title';
  };

  const errorTag = (project) => {
    if (!project) return 'WorkHistoryItem-missing-information-left-margin2';
    const len = project.length;
    if (len > 13 && len < 26) return 'WorkHistoryItem-missing-information-left-margin3';
    if (len <= 13) return 'WorkHistoryItem-missing-information-left-margin2';
    return 'WorkHistoryItem-missing-information-left-margin';
  };

  let title = (
    <div className="WorkHistoryProject-flex" onClick={() => setOpen(true)}>
      <div className={TitleStyle(item.project_name)}>
        <span className="tt">{item.project_name}</span>
      </div>
      { errors.length
        ? <span className={errorTag(item.project_name)}><Alert className='resume-warning-alert' message="Missing Information" showIcon type='error' icon={<CaretUpEmberIcon className="resume-warning-icon" />} /></span>
        : null}
      <span className="material-icons deleteAndIcon openClose" aria-hidden="true">expand_more</span>
    </div>
  );

  if (!item.id) {
    title = (
      <div className="WorkHistoryItem-title">
        Add a project
        <span style={{ flex: 1 }} />
        <span onClick={item.delete} ><WorkExpDeleteIcon /></span>
      </div>
    );
  }

  if (item.$deleted) return null;
  let form = null;
  if (open) {
    if (item.id) {
      title = (
        <div className="WorkHistoryProject-flex" onClick={() => setOpen(false)}>
          <div className={`${TitleStyle(item.project_name)} projectTitle-open`}>
            <span style={{ flex: 1 }}>{item.project_name}</span>
          </div>
          { errors.length
            ? <span className={errorTag(item.project_name)}><Alert className='resume-warning-alert' message="Missing Information" showIcon type='error' icon={<CaretUpEmberIcon className="resume-warning-icon" />} /></span>
            : null}
          <div className="deleteAndIcon">
            {isReadOnly ? null
              : <span className="deleteIconMarginLeft" onClick={item.delete}><WorkExpDeleteIcon /></span>
            }
            <span className="material-icons small-margin-left openClose" aria-hidden="true">expand_less</span>
          </div>
        </div>
      );
    }

    const getErrorMessages = () => errors.map((err, i) => {
      if (data.end_year === 0 && ['end_year', 'end_month'].includes(err.name)) return null;
      return <span key={i} className=''><strong>!</strong>{listProjectErrors(err.name)}</span>;
    });

    const listProjectErrors = (error) => {
      let displayedErrors = '';
      switch (error) {
        case 'project_name':
          displayedErrors = ' Add the name for this project';
          break;
        case 'details':
          displayedErrors = ' Add details for this project';
          break;
        case 'outcome':
          displayedErrors = ' Add the outcome for this project';
          break;
        case 'start_month':
          displayedErrors = ' Add start month for this project';
          break;
        case 'start_year':
          displayedErrors = ' Add start year for this project';
          break;
        case 'end_month':
          displayedErrors = ' Add end month for this project';
          break;
        case 'end_year':
          displayedErrors = ' Add end year for this project';
          break;
        default:
          displayedErrors = ` Add ${error} for this project`;
          break;
      }
      return displayedErrors;
    };

    form = (
      <div className="WorkHistoryItemProjectItem-form" ref={formElm}>
        <div className='listErrors'>
          {!errors.length ? null : getErrorMessages()}
        </div>
        <div className="a"><Input label="Project Name" placeholder="A Very Awesome App" {...bind('project_name')} /></div>
        <div className="b"><Input label="URL or GitHub Repository" placeholder="www.awesomeapp.io" {...bind('url')} /></div>
        <div className="c WorkHistoryItem-date"><Input.Select options={months} label="Starting date" {...bind('start_month')} /></div>
        <div className="d WorkHistoryItem-date"><Input.Select options={years} label="" {...bind('start_year')} /></div>
        <div className="e WorkHistoryItem-date">{data.end_year === 0 ? null : <Input.Select options={months} label="Ending date" {...bind('end_month')} />}</div>
        <div className="f WorkHistoryItem-date">{data.end_year === 0 ? null : <Input.Select options={years} label="" {...bind('end_year')} />}</div>
        <div className="ongoing_project">
          <Checkbox
            checked={data.end_year === 0}
            onChange={(e) => setValue('end_year', e.target.checked ? 0 : (item.end_year === 0 ? null : (item.end_year || null)))}>
            Ongoing project / Still working on this.
          </Checkbox>
        </div>
        <div className="g">
          <Input.TextArea label={<TuringHelpBtn help="Best way is to describe your project starting with the problem statement, what you did to solve it, and the result of the project. Giving concrete examples of how you solved problems at previous jobs are compelling testimonials of your abilities.">Project description</TuringHelpBtn>} {...bind('details')} />
        </div>
        <div className="outcome">
          <Suspense fallback="Loading ...">
            <Input.Editor height={160} label="What was the outcome of the project?" placeholder="What was the outcome of the project?" {...bind('outcome')} />
          </Suspense>
        </div>
        <div className="h">
          <SkillsSelector
            selectSkillAsObject
            autoTagText={`${data.details} ${data.outcome}`}
            label={<TuringHelpBtn help="The skills utilized in the project will be automatically added here. You can add other related tech stacks manually.">Tech stacks</TuringHelpBtn>}
            {...bind('skills')}
            allSkills={baseSkills}
            passed_skills={skillInfo.passed_skills}
            skills_inprogress={skillInfo.skills_inprogress}
            userAllSkills={skillInfo.all_skills}
          />
        </div>
        <div className={`i ${errors.find(err => err.name === 'files') ? 'screenshots-error' : ''}`}>
          <b>Screenshots</b>
          <UploadImage
            project={data}
            onUpdate={onFilesUpdate}
            onRemove={onFilesDelete}
          />
        </div>
      </div>
    );
  }
  return <Step icon={<DirectionIcon active={open} />} className={(open ? 'openStep' : 'step-custom-fill') + ' ' + (item.id ? '' : 'WorkHistoryItemProjectsaddnew')} title={title} description={form} {...props} />;
};

WorkHistoryItemProjectItem.propTypes = {
  item: PropTypes.any,
  parent: PropTypes.any,
  service: PropTypes.any,
  upload_data: PropTypes.array
};

export default WorkHistoryForm;
